import { Component, computed, effect, ElementRef, input, OnDestroy, ViewChild } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { QaiAvatarComponent, QaiSubtitlesComponent } from '@qai/ui';
import { SubtitleKind } from 'src/app/utils/enums/learner/subtitle-kind.enum';

@Component({
  imports: [TranslocoModule, QaiAvatarComponent, QaiSubtitlesComponent],
  selector: 'app-pathway-call-footer',
  standalone: true,
  styleUrls: ['./call-footer.css'],
  templateUrl: './call-footer.html',
})
export class PathwayCallFooterComponent implements OnDestroy {
  /** Caption text to show (e.g. from API). Falls back to FOOTER_SUBTITLE_READY when empty. */
  captionText = input<string>('');
  showAvatar = input<boolean>(true);
  /** Explicit subtitle text to drive qai-subtitles. When empty, falls back to captionText or default copy. */
  subtitleText = input<string>('');
  /** Visual state for subtitles (ready/speaking/listening/muted). */
  subtitleKind = input<SubtitleKind>(SubtitleKind.Ready);
  showAvatarPhoto = input<boolean>(false);
  avatarPhotoUrl = input<string>('');
  showSubtitles = input<boolean>(true);

  @ViewChild(QaiSubtitlesComponent, { read: ElementRef }) subtitlesRef?: ElementRef;

  protected readonly isAvatarSpeaking = computed(() => this.subtitleKind() === SubtitleKind.Speaking);

  private scrollAnimation?: ReturnType<typeof setInterval>;

  constructor() {
    effect(() => {
      const text = this.subtitleText();

      if (this.scrollAnimation) {
        clearInterval(this.scrollAnimation);
        this.scrollAnimation = undefined;
      }

      if (!text?.trim()) return;

      // Wait 150ms for DOM to render new text before measuring dimensions
      setTimeout(() => {
        if (!this.subtitlesRef?.nativeElement) return;
        const host = this.subtitlesRef.nativeElement as HTMLElement;
        const el = host.querySelector('.subtitle-text') as HTMLElement ?? host;
        const labelEl = host.querySelector('.state-label-container') as HTMLElement;

        // Compute available height precisely so host never overflows/clips subtitle-text
        const hostStyle = getComputedStyle(host);
        const hostPaddingTop = parseFloat(hostStyle.paddingTop);
        const labelHeight = labelEl ? labelEl.offsetHeight : 0;
        const elMarginTop = parseFloat(getComputedStyle(el).marginTop);
        const availableHeight = host.clientHeight - hostPaddingTop - labelHeight - elMarginTop;

        el.style.maxHeight = `${availableHeight}px`;
        el.style.overflowY = 'auto';
        el.style.scrollbarWidth = 'none';

        el.scrollTop = 0;

        // Estimate duration: ~160 words per minute average speech rate
        const wordCount = text.trim().split(/\s+/).length;
        const durationMs = Math.max((wordCount / 160) * 60 * 1000, 2000);
        const startTime = Date.now();
        const maxScroll = el.scrollHeight - el.clientHeight;

        if (maxScroll <= 0) return;

        this.scrollAnimation = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / durationMs, 1);
          el.scrollTop = progress * maxScroll;

          if (progress >= 1 && this.scrollAnimation) {
            clearInterval(this.scrollAnimation);
            this.scrollAnimation = undefined;
          }
        }, 50);
      }, 150);
    });
  }

  ngOnDestroy(): void {
    if (this.scrollAnimation) {
      clearInterval(this.scrollAnimation);
    }
  }
}
